import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Table, Input, Breadcrumb, Tag, Row, Col, message, Card, Statistic,
  Typography, Space, Switch, Tooltip, Progress, Empty, Avatar, Select, DatePicker,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  ReloadOutlined, SearchOutlined, SafetyCertificateOutlined, WarningOutlined,
  PhoneOutlined, EnvironmentOutlined, PlayCircleOutlined, CarOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import { walletService, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—');

const REFERRER = {
  self: { color: 'default', label: 'Lui-même' },
  affiliate: { color: 'blue', label: 'Affilié' },
  ambassador: { color: 'purple', label: 'Ambassadeur' },
};

export default function AuditFraude() {
  const [data, setData] = useState({ items: [], total: 0, suspectsTotal: 0, auditedTotal: 0, lastRunAt: null, run: {} });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [suspectOnly, setSuspectOnly] = useState(true);
  const [activity, setActivity] = useState('');
  const [dates, setDates] = useState(null);
  const [q, setQ] = useState('');
  const [cityFilter, setCityFilter] = useState(undefined);
  const [cities, setCities] = useState([]);
  const [exporting, setExporting] = useState(false);
  const pollRef = useRef(null);

  // Liste des villes pour le filtre
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res) ? res : (res?.data || res?.items || res?.cities || []);
        setCities(list.filter((c) => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  const dateParams = () => ({
    start: dates?.[0] ? dates[0].format('YYYY-MM-DD') : undefined,
    end: dates?.[1] ? dates[1].format('YYYY-MM-DD') : undefined,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await walletService.auditFraude({
        page, limit, suspectOnly, activity, q, sort: '-sosTotal',
        cityCode: cityFilter || undefined,
        start: dates?.[0] ? dates[0].format('YYYY-MM-DD') : undefined,
        end: dates?.[1] ? dates[1].format('YYYY-MM-DD') : undefined,
      });
      setData(res);
      return res;
    } catch (e) {
      message.error("Impossible de charger l'audit.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, suspectOnly, activity, q, dates, cityFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Polling pendant qu'un audit tourne
  useEffect(() => {
    if (data?.run?.running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          const res = await fetchData();
          if (!res?.run?.running && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            message.success('Audit terminé.');
          }
        }, 4000);
      }
    }
    return () => { if (pollRef.current && !data?.run?.running) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data?.run?.running, fetchData]);

  const handleRun = async () => {
    try {
      const res = await walletService.runAuditFraude();
      if (res?.started) message.success("Audit lancé en arrière-plan…");
      else if (res?.running) message.info('Un audit est déjà en cours.');
      await fetchData();
    } catch (e) {
      message.error("Échec du lancement de l'audit.");
    }
  };

  // Export Excel : récupère TOUTES les lignes du filtre courant (pagine côté serveur),
  // pas seulement la page visible.
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const rows = [];
      let p = 1;
      const LIM = 200;
      // garde-fou anti-boucle infinie
      for (let guard = 0; guard < 50; guard += 1) {
        const res = await walletService.auditFraude({ page: p, limit: LIM, suspectOnly, activity, q, sort: '-sosTotal', cityCode: cityFilter || undefined, ...dateParams() });
        const items = res?.items || [];
        rows.push(...items);
        if (rows.length >= (res?.total || 0) || items.length === 0) break;
        p += 1;
      }
      if (!rows.length) { message.info('Aucune donnée à exporter.'); return; }

      const refLabel = { self: 'Lui-même', affiliate: 'Affilié', ambassador: 'Ambassadeur' };
      const sheetData = rows.map((r) => ({
        'Nom': [r.firstName, r.lastName].filter(Boolean).join(' '),
        'Téléphone': r.phone || '',
        'Ville': r.cityName || '',
        'Créé par (type)': refLabel[r.referrerType] || r.referrerType || '',
        'Créé par (nom)': r.referrerName || '',
        'Créé par (téléphone)': r.referrerPhone || '',
        'Ambassadeur (chaîne)': r.ambassadorName || '',
        'Compte créé le': r.driverCreatedAt ? dayjs(r.driverCreatedAt).format('DD/MM/YYYY HH:mm') : '',
        '1ère activité SOS/Trf': r.firstActivityAt ? dayjs(r.firstActivityAt).format('DD/MM/YYYY HH:mm') : '',
        'Dernière activité SOS/Trf': r.lastActivityAt ? dayjs(r.lastActivityAt).format('DD/MM/YYYY HH:mm') : '',
        'Courses réelles': `${r.completedCourses}${r.coursesCapped ? '+' : ''}`,
        'Seuil': r.threshold,
        'Suspect': r.isSuspect ? 'OUI' : 'non',
        'SOS - nb': r.sosCount || 0,
        'SOS - total': r.sosTotal || 0,
        'Transfert - nb': r.transfertCount || 0,
        'Transfert - total': r.transfertTotal || 0,
        'Compte actif': r.isActive === false ? 'non' : 'oui',
        'Vérif Yandex': r.yandexError ? 'échouée' : 'ok',
        'driverProfilId': r.driverProfilId,
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Audit Fraude');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `audit-fraude-${dayjs().format('YYYY-MM-DD-HHmm')}.xlsx`);
      message.success(`${rows.length} ligne(s) exportée(s).`);
    } catch (e) {
      message.error("Échec de l'export Excel.");
    } finally {
      setExporting(false);
    }
  };

  const run = data?.run || {};
  const progress = run.total ? Math.round((run.processed / run.total) * 100) : 0;

  const columns = [
    {
      title: 'Chauffeur',
      key: 'chauffeur',
      render: (_, r) => (
        <Space>
          <Avatar style={{ backgroundColor: r.isSuspect ? '#fff1f0' : '#f6ffed', color: r.isSuspect ? '#cf1322' : '#389e0d' }}>
            {(r.firstName || r.phone || '?').charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div><Text strong>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</Text>{' '}
              {r.isActive === false && <Tag color="default">désactivé</Tag>}</div>
            <Text type="secondary" style={{ fontSize: 12 }}><PhoneOutlined /> {r.phone || '—'}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Créé par',
      key: 'referrer',
      render: (_, r) => {
        const ref = REFERRER[r.referrerType] || REFERRER.self;
        return (
          <div>
            <Tag color={ref.color}>{ref.label}</Tag>
            {r.referrerType !== 'self' && (
              <div style={{ fontSize: 12 }}>
                <Text strong>{r.referrerName || '—'}</Text>
                {r.referrerPhone && <Text type="secondary"> · {r.referrerPhone}</Text>}
                {r.ambassadorName && <div><Text type="secondary" style={{ fontSize: 11 }}>↳ Amb. {r.ambassadorName}</Text></div>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: <Tooltip title="Nombre de courses terminées (Yandex)"><span><CarOutlined /> Courses</span></Tooltip>,
      key: 'courses',
      align: 'center',
      sorter: (a, b) => a.completedCourses - b.completedCourses,
      render: (_, r) => {
        if (r.yandexError) return <Tag color="orange">vérif. échouée</Tag>;
        const label = `${r.completedCourses}${r.coursesCapped ? '+' : ''} / ${r.threshold}`;
        return <Tag color={r.isSuspect ? 'red' : 'green'}>{label}</Tag>;
      },
    },
    {
      title: 'SOS Crédit',
      key: 'sos',
      align: 'right',
      sorter: (a, b) => a.sosTotal - b.sosTotal,
      render: (_, r) => r.sosCount ? <Tooltip title={`${r.sosCount} opération(s)`}><Text>{fmt(r.sosTotal)}<br /><Text type="secondary" style={{ fontSize: 11 }}>{r.sosCount}×</Text></Text></Tooltip> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Transfert UV',
      key: 'transfert',
      align: 'right',
      sorter: (a, b) => a.transfertTotal - b.transfertTotal,
      render: (_, r) => r.transfertCount ? <Tooltip title={`${r.transfertCount} transfert(s)`}><Text>{fmt(r.transfertTotal)}<br /><Text type="secondary" style={{ fontSize: 11 }}>{r.transfertCount}×</Text></Text></Tooltip> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Ville',
      dataIndex: 'cityName',
      key: 'cityName',
      render: (v) => v ? <Text type="secondary"><EnvironmentOutlined /> {v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Dates',
      key: 'dates',
      sorter: (a, b) => new Date(a.lastActivityAt || 0) - new Date(b.lastActivityAt || 0),
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div><Text type="secondary">Compte&nbsp;:</Text> {fmtDate(r.driverCreatedAt)}</div>
          <div><Text type="secondary">SOS/Trf&nbsp;:</Text>{' '}
            {r.firstActivityAt && r.lastActivityAt && fmtDate(r.firstActivityAt) !== fmtDate(r.lastActivityAt)
              ? `${fmtDate(r.firstActivityAt)} → ${fmtDate(r.lastActivityAt)}`
              : fmtDate(r.lastActivityAt)}
          </div>
        </div>
      ),
    },
    {
      title: 'Statut',
      key: 'statut',
      align: 'center',
      render: (_, r) => r.isSuspect
        ? <Tag icon={<WarningOutlined />} color="error">SUSPECT</Tag>
        : <Tag icon={<SafetyCertificateOutlined />} color="success">OK</Tag>,
    },
  ];

  return (
    <div style={{ padding: 8 }}>
      <Breadcrumb style={{ marginBottom: 16 }} items={[
        { title: <Link to="/dashboard">Accueil</Link> },
        { title: 'Wallet' },
        { title: 'Audit Fraude' },
      ]} />

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><SafetyCertificateOutlined /> Audit Fraude</Title>
          <Text type="secondary">Chauffeurs ayant fait du SOS Crédit / transfert UV sans courses réelles</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Actualiser</Button>
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} loading={exporting} disabled={!data.total}>
              Exporter (Excel)
            </Button>
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleRun} loading={run.running}>
              {run.running ? 'Audit en cours…' : "Lancer l'audit"}
            </Button>
          </Space>
        </Col>
      </Row>

      {run.running && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text>Analyse Yandex en cours… {run.processed}/{run.total} chauffeurs</Text>
          <Progress percent={progress} status="active" />
        </Card>
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8}>
          <Card size="small"><Statistic title="Suspects (sans course)" value={data.suspectsTotal} valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} /></Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small"><Statistic title="Acteurs SOS/transfert audités" value={data.auditedTotal} /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small"><Statistic title="Dernier audit" valueRender={() => <Text>{data.lastRunAt ? dayjs(data.lastRunAt).fromNow() : 'jamais'}</Text>} /></Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            allowClear placeholder="Rechercher (nom, téléphone, ID)…" prefix={<SearchOutlined />}
            style={{ width: 280 }} value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
          />
          <Select
            value={activity}
            style={{ width: 180 }}
            onChange={(v) => { setPage(1); setActivity(v); }}
            options={[
              { value: '', label: 'Toute activité' },
              { value: 'sos', label: 'SOS Crédit' },
              { value: 'transfert', label: 'Transfert UV' },
            ]}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 200 }}
            placeholder={<span><EnvironmentOutlined style={{ marginRight: 6 }} />Filtrer par ville</span>}
            value={cityFilter}
            onChange={(v) => { setPage(1); setCityFilter(v); }}
            optionFilterProp="label"
            options={cities.map((c) => ({ value: c.code, label: c.name }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            placeholder={['Activité du', 'au']}
            value={dates}
            onChange={(v) => { setPage(1); setDates(v); }}
          />
          <Space>
            <Switch checked={suspectOnly} onChange={(v) => { setPage(1); setSuspectOnly(v); }} />
            <Text>Suspects uniquement</Text>
          </Space>
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey="driverProfilId"
          loading={loading}
          dataSource={data.items}
          columns={columns}
          locale={{ emptyText: <Empty description={data.lastRunAt ? 'Aucun résultat' : "Lance l'audit pour générer les résultats"} /> }}
          pagination={{
            current: page, pageSize: limit, total: data.total,
            showTotal: (t) => `${t} chauffeur(s)`,
            onChange: (p) => setPage(p),
          }}
        />
      </Card>
    </div>
  );
}
